import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { leadApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import DataTable from '../../components/DataTable.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Badge from '../../components/Badge.jsx';
import ScoreGauge from '../../components/ScoreGauge.jsx';
import { Select, TextInput } from '../../components/Field.jsx';
import { formatDate, fullName } from '../../utils/format.js';
import { LEAD_STATUSES, TEMPERATURES } from '../../constants.js';


/** Shared by teleprospection ("my qualifications") and sales ("leads received"). */
const LeadListPage = ({ titleKey = 'leads.title', newLeadButton = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState({ q: '', status: '', temperature: '', toRecall: false });
  const { data, loading } = useApi(() => leadApi.list(query), [query]);

  const patch = (part) => setQuery((current) => ({ ...current, ...part }));

  const columns = [
    {
      key: 'customerName',
      header: t('customers.companyName'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.customerName ?? '-'}</span>
          <span className="cell-muted">{row.answers?.contact_full_name ?? ''}</span>
        </div>
      ),
    },
    {
      key: 'score',
      header: t('leads.score'),
      render: (row) => <ScoreGauge score={row.score ?? 0} temperature={row.temperature ?? 'COLD'} compact />,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => {
        const date = row.answers?.callback_date;
        const due = date && date <= new Date().toISOString().slice(0, 10);
        return (
          <div className="cell-stack">
            <Badge value={row.status} label={t(`leads.status.${row.status}`)} />
            {date ? (
              <span className={due ? 'callback callback--due' : 'callback'}>
                {t('leads.recallOn', { date: formatDate(date, i18n.resolvedLanguage) })}
              </span>
            ) : null}
          </div>
        );
      },
    },
    { key: 'telepro', header: t('leads.createdBy'), render: (row) => fullName(row.telepro) },
    { key: 'commercial', header: t('leads.assignedTo'), render: (row) => fullName(row.commercial) },
    {
      key: 'createdAt',
      header: t('common.date'),
      render: (row) => formatDate(row.createdAt, i18n.resolvedLanguage),
    },
  ];

  return (
    <>
      <PageHeader
        title={t(titleKey)}
        actions={
          newLeadButton ? (
            <button type="button" className="btn btn--primary" onClick={() => navigate('/telepro/qualification')}>
              {t('leads.new')}
            </button>
          ) : null
        }
      />

      <Toolbar>
        {/* The day's work: calls whose agreed call-back date has arrived. */}
        <div className="segmented" role="group">
          <button
            type="button"
            className={!query.toRecall ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => patch({ toRecall: false })}
          >
            {t('common.all')}
          </button>
          <button
            type="button"
            className={query.toRecall ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => patch({ toRecall: true })}
          >
            {t('leads.toRecall')}
          </button>
        </div>
        <TextInput value={query.q} onChange={(value) => patch({ q: value })} placeholder={t('common.search')} />
        <Select
          value={query.status}
          onChange={(value) => patch({ status: value })}
          placeholder={t('common.all')}
          options={LEAD_STATUSES.map((status) => ({ value: status, label: t(`leads.status.${status}`) }))}
        />
        <Select
          value={query.temperature}
          onChange={(value) => patch({ temperature: value })}
          placeholder={t('common.all')}
          options={TEMPERATURES.map((item) => ({ value: item, label: t(`leads.temperatures.${item}`) }))}
        />
        <span className="toolbar__count">
          {data?.pagination?.total ?? 0} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={loading}
        emptyTitle={query.toRecall ? t('leads.noRecall') : t('leads.empty')}
        onRowClick={(row) =>
          navigate(user.role === 'TELEPRO' ? `/telepro/qualification/${row.id}` : `/leads/${row.id}`)
        }
      />
    </>
  );
};

export default LeadListPage;
